import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const COLS = 3;

const Shard = ({ poem, lines, index, rows }) => {
  const col = index % COLS;
  const row = Math.floor(index / COLS);
  const boxRef = useRef(null);
  const [tick, setTick] = useState(0);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const shard = box.parentElement;
    let scale = 1;
    box.style.setProperty("--s", scale);
    while (box.offsetHeight > shard.clientHeight && scale > 0.35) {
      scale = Math.round((scale - 0.03) * 100) / 100;
      box.style.setProperty("--s", scale);
    }
  }, [tick, poem.id, index]);

  useEffect(() => {
    const onResize = () => setTick((t) => t + 1);
    window.addEventListener("resize", onResize);
    document.fonts.ready.then(() => setTick((t) => t + 1));
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div
      data-testid={`shard-${poem.id}-${index}`}
      className="shard"
      style={{
        backgroundImage: `url(${poem.image})`,
        backgroundSize: `${COLS * 100}% ${rows * 100}%`,
        backgroundPosition: `${(col / (COLS - 1)) * 100}% ${
          rows > 1 ? (row / (rows - 1)) * 100 : 0
        }%`,
      }}
    >
      <div className="shard-lines" ref={boxRef}>
        {index === 0 && (
          <>
            <p className="byron line line-title" data-testid={`poem-title-${poem.id}`}>
              {poem.title}
            </p>
            <p className="byron line line-date" data-testid={`poem-date-${poem.id}`}>
              {poem.date}
            </p>
          </>
        )}
        {lines.map((line, j) => (
          <p key={j} className="byron line">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
};

export const PoemOverlay = ({ poem, onClose, onPrev, onNext }) => {
  const rows = Math.ceil(poem.shards.length / COLS);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="overlay" data-testid="poem-overlay" onClick={onClose}>
      <button
        className="overlay-nav overlay-prev"
        data-testid="overlay-prev"
        aria-label="Previous poem"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
      >
        <ChevronLeft size={34} strokeWidth={1.4} />
      </button>

      <div
        className="overlay-stage"
        data-testid="overlay-stage"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shard-grid" style={{ "--cols": COLS }}>
          {poem.shards.map((lines, i) => (
            <Shard key={i} poem={poem} lines={lines} index={i} rows={rows} />
          ))}
        </div>
      </div>

      <button
        className="overlay-nav overlay-next"
        data-testid="overlay-next"
        aria-label="Next poem"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
      >
        <ChevronRight size={34} strokeWidth={1.4} />
      </button>

      <button
        className="overlay-close"
        data-testid="overlay-close"
        aria-label="Close poem"
        onClick={onClose}
      >
        <X size={22} strokeWidth={1.4} />
      </button>
    </div>
  );
};
